import {useMemo, useState} from "react";
import {InvoiceEntity} from "@ots-frontend/ots-data";
import {
  DataTableFilterConfig,
  useDataTableFilter,
  UseDataTableFilterProps,
  UseDataTableFilterResult
} from "../DataTable/useDataTableFilter";
import {useTranslation} from "react-i18next";
import {useDebouncedValue} from "@mantine/hooks";
import {getFormattedDatabaseCode} from "@ots-frontend/ots-ui";

export type UseInvoiceDataTableFilterProps = Omit<UseDataTableFilterProps<InvoiceEntity>, 'filterConfig'>

export function useInvoiceDataTableFilter(props: UseInvoiceDataTableFilterProps): UseDataTableFilterResult<InvoiceEntity> {
  const {records} = props;

  const {t} = useTranslation(["common"])

  const [invoiceNumberQuery, setInvoiceNumberQuery] = useState<string | undefined>(undefined);
  const [invoiceNumberDebouncedQuery] = useDebouncedValue(invoiceNumberQuery, 200);

  const [statusQuery, setStatusQuery] = useState<string | undefined>(undefined);
  const [statusDebouncedQuery] = useDebouncedValue(statusQuery, 200);

  const [categoryQuery, setCategoryQuery] = useState<string | undefined>(undefined);
  const [categoryDebouncedQuery] = useDebouncedValue(categoryQuery, 200);

  const [methodQuery, setMethodQuery] = useState<string | undefined>(undefined);
  const [methodDebouncedQuery] = useDebouncedValue(methodQuery, 200);

  const [operatorQuery, setOperatorQuery] = useState<string | undefined>(undefined);
  const [operatorDebouncedQuery] = useDebouncedValue(operatorQuery, 200);

  const [vendorQuery, setVendorQuery] = useState<string | undefined>(undefined);
  const [vendorDebouncedQuery] = useDebouncedValue(vendorQuery, 200);

  const [secondaryVendorQuery, setSecondaryVendorQuery] = useState<string | undefined>(undefined);
  const [secondaryVendorDebouncedQuery] = useDebouncedValue(secondaryVendorQuery, 200);

  const filterConfig: DataTableFilterConfig<InvoiceEntity> = useMemo(() => {
    return {
      "invoiceNumber": {
        setQuery: setInvoiceNumberQuery,
        query: invoiceNumberQuery,
        debouncedQuery: invoiceNumberDebouncedQuery,
        filter: (record, query) => !record.invoiceNumber || !record.invoiceNumber.toLowerCase().includes(query.toLowerCase())
      },
      "status": {
        setQuery: setStatusQuery,
        query: statusQuery,
        debouncedQuery: statusDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.status, t)
          return !record.status.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase()))
        }
      },
      "category": {
        setQuery: setCategoryQuery,
        query: categoryQuery,
        debouncedQuery: categoryDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.category, t)
          return !record.category || (!record.category.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase())))
        }
      },
      "method": {
        setQuery: setMethodQuery,
        query: methodQuery,
        debouncedQuery: methodDebouncedQuery,
        filter: (record, query) => {
          const display = getFormattedDatabaseCode(record.method, t)
          return !record.method || (!record.method.code.toLowerCase().includes(query.toLowerCase()) && (!display || !display.toLowerCase().includes(query.toLowerCase())))
        }
      },
      "operator": {
        setQuery: setOperatorQuery,
        query: operatorQuery,
        debouncedQuery: operatorDebouncedQuery,
        filter: (record, query) => !record.operatorName || !record.operatorName.toLowerCase().includes(query.toLowerCase())
      },
      "vendor": {
        setQuery: setVendorQuery,
        query: vendorQuery,
        debouncedQuery: vendorDebouncedQuery,
        filter: (record, query) => !record.vendorName || !record.vendorName.toLowerCase().includes(query.toLowerCase())
      },
      "secondaryVendor": {
        setQuery: setSecondaryVendorQuery,
        query: secondaryVendorQuery,
        debouncedQuery: secondaryVendorDebouncedQuery,
        filter: (record, query) => !record.secondaryVendorName || !record.secondaryVendorName.toLowerCase().includes(query.toLowerCase())
      },
    }
  }, [
    invoiceNumberQuery,
    invoiceNumberDebouncedQuery,
    statusQuery,
    statusDebouncedQuery,
    categoryQuery,
    categoryDebouncedQuery,
    methodQuery,
    methodDebouncedQuery,
    operatorQuery,
    operatorDebouncedQuery,
    vendorQuery,
    vendorDebouncedQuery,
    secondaryVendorQuery,
    secondaryVendorDebouncedQuery,
    t
  ])

  const {filteredRecords} = useDataTableFilter({
    records: records,
    filterConfig: filterConfig,
  })

  return {
    filteredRecords: filteredRecords,
    filterConfig: filterConfig
  };
}
